import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  Paper,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootStore } from '../../../Store';
import { 
  PreorderCloseEventRequest, 
  PreorderCloseStockDistribution,
} from '../../../actions/preorderEventActions/PreorderEventActionsTypes';

// Types for internal state management
interface EditableItemDistribution {
  itemId: number;
  itemName: string;
  itemCode: string;
  totalAvailableStock: number;
  distributions: {
    vendorId: number;
    vendorName: string;
    assignedQuantity: number;
  }[];
}

interface DistributionState {
  items: EditableItemDistribution[];
  unassignedStock: { [itemId: number]: number };
}

interface PreorderSuggestedDistributionModalProps {
  open: boolean;
  onClose?: () => void;
  onConfirm?: (payload: PreorderCloseEventRequest) => void;
}

export const PreorderSuggestedDistributionModal: React.FC<PreorderSuggestedDistributionModalProps> = ({ 
  open, 
  onClose,
  onConfirm 
}) => {
    const { t } = useTranslation('global');
    const preorderEventState = useSelector((state: RootStore) => state.preorderEvents);
    const { suggestedPreorderCloseDistribution, preorderEventDetails } = preorderEventState;

    // Initialize editable distribution state
    const [distributionState, setDistributionState] = useState<DistributionState>({
      items: [],
      unassignedStock: {}
    });

    // Transform suggested distribution into editable format
    useEffect(() => {
      if (suggestedPreorderCloseDistribution?.vendors) {
        const itemsMap = new Map<number, EditableItemDistribution>();
        
        // Process each vendor and their recommended items
        suggestedPreorderCloseDistribution.vendors.forEach(vendor => {
          vendor.recommendedItems.forEach(item => {
            const totalStock = item.currentStock + item.currentDiscountStock;
            const recommendedStock = item.recommendedStockToAdd + item.recommendedDiscountStockToAdd;
            
            if (!itemsMap.has(item.id)) {
              itemsMap.set(item.id, {
                itemId: item.id,
                itemName: item.name,
                itemCode: item.code,
                totalAvailableStock: totalStock,
                distributions: []
              });
            }
            
            const editableItem = itemsMap.get(item.id)!;
            editableItem.distributions.push({
              vendorId: vendor.vendorId,
              vendorName: vendor.vendorName,
              assignedQuantity: recommendedStock
            });
          });
        });

        const items = Array.from(itemsMap.values());
        const unassignedStock: { [itemId: number]: number } = {};
        
        // Calculate initial unassigned stock
        items.forEach(item => {
          const totalAssigned = item.distributions.reduce((sum, dist) => sum + dist.assignedQuantity, 0);
          unassignedStock[item.itemId] = item.totalAvailableStock - totalAssigned;
        });

        setDistributionState({ items, unassignedStock });
      }
    }, [suggestedPreorderCloseDistribution]);

    // Handle quantity change for an item-vendor distribution
    const handleQuantityChange = (itemId: number, vendorId: number, newQuantity: number) => {
      if (newQuantity < 0) return;

      setDistributionState(prev => {
        const newItems = prev.items.map(item => {
          if (item.itemId === itemId) {
            const newDistributions = item.distributions.map(dist => {
              if (dist.vendorId === vendorId) {
                return { ...dist, assignedQuantity: newQuantity };
              }
              return dist;
            });
            return { ...item, distributions: newDistributions };
          }
          return item;
        });

        // Recalculate unassigned stock for this item
        const newUnassignedStock = { ...prev.unassignedStock };
        const item = newItems.find(i => i.itemId === itemId);
        if (item) {
          const totalAssigned = item.distributions.reduce((sum, dist) => sum + dist.assignedQuantity, 0);
          newUnassignedStock[itemId] = item.totalAvailableStock - totalAssigned;
        }

        return { items: newItems, unassignedStock: newUnassignedStock };
      });
    };

    // Check if distribution is valid (no negative unassigned stock)
    const isValidDistribution = useMemo(() => {
      return Object.values(distributionState.unassignedStock).every(stock => stock >= 0);
    }, [distributionState.unassignedStock]);

    // Check if there's any unassigned stock
    const hasUnassignedStock = useMemo(() => {
      return Object.values(distributionState.unassignedStock).some(stock => stock > 0);
    }, [distributionState.unassignedStock]);

    // Convert current state to API payload format
    const generatePayload = (): PreorderCloseEventRequest => {
      const vendorMap = new Map<number, PreorderCloseStockDistribution>();
      
      distributionState.items.forEach(item => {
        item.distributions.forEach(dist => {
          if (dist.assignedQuantity > 0) {
            if (!vendorMap.has(dist.vendorId)) {
              vendorMap.set(dist.vendorId, {
                vendorIdToReceiveStock: dist.vendorId,
                itemList: []
              });
            }
            
            vendorMap.get(dist.vendorId)!.itemList.push({
              preorderItemId: item.itemId,
              quantityToRelease: dist.assignedQuantity
            });
          }
        });
      });

      return {
        preorderEventId: preorderEventDetails?.id || 0,
        vendorsToObtainStocks: Array.from(vendorMap.values())
      };
    };

    const handleConfirm = () => {
      if (isValidDistribution && onConfirm) {
        const payload = generatePayload();
        console.log('Confirming with payload:', payload);
        onConfirm(payload);
      }
    };

    const handleReset = () => {
      // Reset to suggested values by re-running the effect
      if (suggestedPreorderCloseDistribution?.vendors) {
        const itemsMap = new Map<number, EditableItemDistribution>();
        
        suggestedPreorderCloseDistribution.vendors.forEach(vendor => {
          vendor.recommendedItems.forEach(item => {
            const totalStock = item.currentStock + item.currentDiscountStock;
            const recommendedStock = item.recommendedStockToAdd + item.recommendedDiscountStockToAdd;
            
            if (!itemsMap.has(item.id)) {
              itemsMap.set(item.id, {
                itemId: item.id,
                itemName: item.name,
                itemCode: item.code,
                totalAvailableStock: totalStock,
                distributions: []
              });
            }
            
            const editableItem = itemsMap.get(item.id)!;
            editableItem.distributions.push({
              vendorId: vendor.vendorId,
              vendorName: vendor.vendorName,
              assignedQuantity: recommendedStock
            });
          });
        });

        const items = Array.from(itemsMap.values());
        const unassignedStock: { [itemId: number]: number } = {};
        
        items.forEach(item => {
          const totalAssigned = item.distributions.reduce((sum, dist) => sum + dist.assignedQuantity, 0);
          unassignedStock[item.itemId] = item.totalAvailableStock - totalAssigned;
        });

        setDistributionState({ items, unassignedStock });
      }
    };

    return (
      <Dialog open={open} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{t('preorderEventSuggestedDistribution.modal-title')}</Typography>
            {hasUnassignedStock && (
              <Chip 
                label={t('preorderEventSuggestedDistribution.unassigned-stock-detected')} 
                color="secondary" 
                size="small" 
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ minHeight: '400px' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" style={{ marginBottom: '16px' }}>
                {t('preorderEventSuggestedDistribution.modal-description')}
              </Typography>
              
              {!isValidDistribution && (
                <Box mb={2}>
                  <Paper style={{ padding: '8px', backgroundColor: '#ffebee' }}>
                    <Typography variant="body2" color="error">
                      {t('preorderEventSuggestedDistribution.over-allocated-warning')}
                    </Typography>
                  </Paper>
                </Box>
              )}

              {distributionState.items.length === 0 ? (
                <Typography variant="body1">
                  {t('preorderEventSuggestedDistribution.no-distribution-available')}
                </Typography>
              ) : (
                distributionState.items.map((item) => (
                  <Accordion key={item.itemId} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <Typography variant="h6">
                          {item.itemName} ({item.itemCode})
                        </Typography>
                        <Box display="flex" alignItems="center" mr={2}>
                          <Chip 
                            label={`${t('preorderEventSuggestedDistribution.total-label')}: ${item.totalAvailableStock}`} 
                            size="small" 
                            style={{ marginRight: '8px' }}
                          />
                          <Chip 
                            label={`${t('preorderEventSuggestedDistribution.unassigned-label')}: ${distributionState.unassignedStock[item.itemId] || 0}`}
                            color={distributionState.unassignedStock[item.itemId] > 0 ? "secondary" : 
                                   distributionState.unassignedStock[item.itemId] < 0 ? "default" : "primary"}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        {item.distributions.map((distribution) => (
                          <Grid item xs={12} sm={6} md={4} key={distribution.vendorId}>
                            <Paper style={{ padding: '16px' }}>
                              <Typography variant="subtitle1" gutterBottom>
                                {distribution.vendorName}
                              </Typography>
                              <TextField
                                label={t('preorderEventSuggestedDistribution.quantity-to-assign')}
                                type="number"
                                value={distribution.assignedQuantity}
                                onChange={(e) => handleQuantityChange(
                                  item.itemId, 
                                  distribution.vendorId, 
                                  parseInt(e.target.value) || 0
                                )}
                                fullWidth
                                inputProps={{ min: 0, max: item.totalAvailableStock }}
                                error={distributionState.unassignedStock[item.itemId] < 0}
                              />
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReset} color="default">
            {t('preorderEventSuggestedDistribution.reset-to-suggested')}
          </Button>
          <Button onClick={onClose} color="secondary">
            {t('preorderEventSuggestedDistribution.cancel')}
          </Button>
          <Button 
            onClick={handleConfirm} 
            color="primary" 
            variant="contained"
            disabled={!isValidDistribution || hasUnassignedStock}
          >
            {t('preorderEventSuggestedDistribution.confirm-distribution')}
          </Button>
        </DialogActions>
      </Dialog>
    );
};